import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Snackbar,
} from '@mui/material';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const apiUrl = (path) => `${API_BASE}${path}`;

const emptyConfig = {
  tailscaleIp: '',
  tailscaleHost: '',
  sshUser: '',
  pingDestination: '',
  remotePgDumpLocation: '',
};

function getAuthHeaders() {
  const candidates = ['token', 'jwt', 'jwtToken', 'accessToken', 'authToken'];
  let token = null;

  for (const key of candidates) {
    const value = window.localStorage.getItem(key);
    if (value && value.trim()) {
      token = value.trim();
      break;
    }
  }

  if (!token) return {};
  return token.toLowerCase().startsWith('bearer ')
    ? { Authorization: token }
    : { Authorization: `Bearer ${token}` };
}

function humanBytes(bytes) {
  if (bytes == null || isNaN(bytes)) return '-';
  const thresh = 1024;
  if (bytes < thresh) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let unitIndex = -1;
  let value = bytes;
  do {
    value /= thresh;
    unitIndex += 1;
  } while (value >= thresh && unitIndex < units.length - 1);
  return `${value.toFixed(2)} ${units[unitIndex]}`;
}

function formatDate(timestamp) {
  if (!timestamp) return '-';
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return String(timestamp);
    return date.toLocaleString();
  } catch {
    return String(timestamp);
  }
}

function statusColor(status) {
  switch (status?.toLowerCase()) {
    case 'success':
    case 'running':
    case 'connected':
      return 'success';
    case 'error':
    case 'failed':
    case 'fail':
    case 'stopped':
      return 'error';
    case 'warning':
    case 'starting':
      return 'warning';
    default:
      return 'default';
  }
}

export default function DataIntegrity() {
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [config, setConfig] = useState(emptyConfig);
  const [tailscaleStatus, setTailscaleStatus] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });

  const requestJson = useCallback(async (path, options = {}) => {
    const headers = {
      ...getAuthHeaders(),
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    };
    const response = await fetch(apiUrl(path), {
      credentials: 'include',
      ...options,
      headers,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`${response.status}${text ? ` - ${text}` : ''}`);
    }

    return response.json();
  }, []);

  const refreshTailscaleStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const statusData = await requestJson('/api/admin/data-integrity/tailscale/status');
      setTailscaleStatus(statusData);
      if (statusData.config) {
        setConfig({ ...emptyConfig, ...statusData.config });
      }
    } catch (err) {
      setStatusError(err.message || 'Unable to read Tailscale status.');
    } finally {
      setStatusLoading(false);
    }
  }, [requestJson]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, logsData, configData] = await Promise.all([
        requestJson('/api/admin/backups/summary'),
        requestJson('/api/admin/backups/logs?limit=200'),
        requestJson('/api/admin/data-integrity/config'),
      ]);

      setSummary(summaryData);
      setLogs(logsData.records || []);
      setConfig({ ...emptyConfig, ...configData });
      await refreshTailscaleStatus();
    } catch (err) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [requestJson, refreshTailscaleStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConfigChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const saved = await requestJson('/api/admin/data-integrity/config', {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      setConfig({ ...emptyConfig, ...saved });
      setNotice({ open: true, message: 'Data integrity settings saved.', severity: 'success' });
      await refreshTailscaleStatus();
    } catch (err) {
      setNotice({ open: true, message: err.message || 'Failed to save settings.', severity: 'error' });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleConnectTailscale = async () => {
    setConnecting(true);
    setStatusError(null);
    try {
      const response = await requestJson('/api/admin/data-integrity/tailscale/connect', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setTailscaleStatus(response);
      if (response.config) {
        setConfig({ ...emptyConfig, ...response.config });
      }
      setNotice({ open: true, message: response.message || 'Tailscale connect completed.', severity: 'success' });
    } catch (err) {
      setNotice({ open: true, message: err.message || 'Tailscale connect failed.', severity: 'error' });
      await refreshTailscaleStatus();
    } finally {
      setConnecting(false);
    }
  };

  const databases = summary?.databases
    ? [...summary.databases].sort((a, b) => {
        const aTs = a.newestTimestamp ? new Date(a.newestTimestamp).getTime() : 0;
        const bTs = b.newestTimestamp ? new Date(b.newestTimestamp).getTime() : 0;
        return bTs - aTs;
      })
    : [];

  const recentLogs = logs.slice(-20).reverse();
  const tailscale = tailscaleStatus?.tailscale;
  const ping = tailscaleStatus?.ping;
  const connectionLabel = tailscale?.connected
    ? 'connected'
    : tailscale?.cliAvailable
      ? tailscale?.backendState || 'not connected'
      : 'unavailable';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 'none', mx: 0, flex: 1, minHeight: 750 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={fetchData}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={fetchData}>Retry</Button>}
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {!loading && !error && summary && (
        <>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 14px 30px rgba(15,23,42,0.06)',
              p: 3,
            }}
          >
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
              <Box sx={{ flex: 1.2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Typography variant="h6">Tailscale</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={refreshTailscaleStatus} disabled={statusLoading || connecting}>
                      {statusLoading ? 'Checking...' : 'Check'}
                    </Button>
                    <Button size="small" variant="contained" onClick={handleConnectTailscale} disabled={connecting}>
                      {connecting ? 'Connecting...' : 'Connect'}
                    </Button>
                  </Stack>
                </Stack>

                <Divider sx={{ my: 2 }} />

                {statusError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {statusError}
                  </Alert>
                )}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip label={connectionLabel} color={statusColor(connectionLabel)} size="small" />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Local IP</Typography>
                    <Typography variant="body2">{tailscale?.localIp || config.tailscaleIp || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Host</Typography>
                    <Typography variant="body2">{tailscale?.self?.hostName || config.tailscaleHost || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Ping</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={ping?.destination ? (ping.success ? 'success' : 'failed') : 'not set'}
                        color={ping?.destination ? (ping.success ? 'success' : 'error') : 'default'}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>

                {ping?.output && (
                  <Typography sx={{ mt: 1.5, fontSize: 12, color: 'rgba(15,17,21,0.62)', whiteSpace: 'pre-wrap' }}>
                    {ping.output}
                  </Typography>
                )}
                {ping?.error && (
                  <Typography sx={{ mt: 1.5, fontSize: 12, color: '#b42318', whiteSpace: 'pre-wrap' }}>
                    {ping.error}
                  </Typography>
                )}
              </Box>

              <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', lg: 'block' } }} />

              <Box sx={{ flex: 1.8 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Typography variant="h6">Connection Settings</Typography>
                  <Button size="small" variant="contained" onClick={handleSaveConfig} disabled={savingConfig}>
                    {savingConfig ? 'Saving...' : 'Save'}
                  </Button>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1.5,
                  }}
                >
                  <TextField
                    label="Tailscale IP"
                    size="small"
                    value={config.tailscaleIp}
                    onChange={(event) => handleConfigChange('tailscaleIp', event.target.value)}
                  />
                  <TextField
                    label="Tailscale Host"
                    size="small"
                    value={config.tailscaleHost}
                    onChange={(event) => handleConfigChange('tailscaleHost', event.target.value)}
                  />
                  <TextField
                    label="SSH User"
                    size="small"
                    value={config.sshUser}
                    onChange={(event) => handleConfigChange('sshUser', event.target.value)}
                  />
                  <TextField
                    label="Ping Destination"
                    size="small"
                    value={config.pingDestination}
                    onChange={(event) => handleConfigChange('pingDestination', event.target.value)}
                  />
                  <TextField
                    label="Remote pg_dump Location"
                    size="small"
                    value={config.remotePgDumpLocation}
                    onChange={(event) => handleConfigChange('remotePgDumpLocation', event.target.value)}
                    sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
                  />
                </Box>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 14px 30px rgba(15,23,42,0.06)',
              p: 3,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Offload status
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  DB Offload
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="textSecondary">Last Success: {formatDate(summary.offload?.db?.lastSuccess)}</Typography>
                  <Typography variant="body2" color="textSecondary">Last Error: {formatDate(summary.offload?.db?.lastError)}</Typography>
                  <Typography variant="body2" color="textSecondary">Last Size: {humanBytes(summary.offload?.db?.lastSizeBytes)}</Typography>
                  <Typography variant="body2" color="textSecondary">Remote Path: {config.remotePgDumpLocation || '-'}</Typography>
                </Stack>
              </Box>
              <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Media Offload
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="textSecondary">Last Success: {formatDate(summary.offload?.media?.lastSuccess)}</Typography>
                  <Typography variant="body2" color="textSecondary">Last Error: {formatDate(summary.offload?.media?.lastError)}</Typography>
                  <Typography variant="body2" color="textSecondary">Last Size: {humanBytes(summary.offload?.media?.lastSizeBytes)}</Typography>
                </Stack>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 14px 30px rgba(15,23,42,0.06)',
              p: 2,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ px: 1, pt: 1 }}>
              <Typography variant="h6" gutterBottom>
                Backups & Activity
              </Typography>
              <Typography sx={{ fontSize: 14, color: 'rgba(15,17,21,0.62)' }}>
                Switch tabs to view database dump coverage and recent backup events.
              </Typography>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="Backup data views"
              sx={{ px: 1, mt: 1 }}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <Tab id="backup-summary-tab" aria-controls="backup-summary-panel" label={`Backup Summary (${databases.length})`} />
              <Tab id="recent-activity-tab" aria-controls="recent-activity-panel" label={`Recent Activity (${recentLogs.length})`} />
            </Tabs>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {activeTab === 0 && (
                <TableContainer
                  id="backup-summary-panel"
                  role="tabpanel"
                  aria-labelledby="backup-summary-tab"
                  sx={{ overflowX: 'auto', overflowY: 'auto', height: '100%' }}
                >
                  <Table size="small" aria-label="backup summary table" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Database</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">7d</TableCell>
                        <TableCell align="right">30d</TableCell>
                        <TableCell>Last Backup</TableCell>
                        <TableCell align="right">Size</TableCell>
                        <TableCell>Last Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {databases.map((db) => (
                        <TableRow key={db.db} hover>
                          <TableCell>{db.db}</TableCell>
                          <TableCell align="right">{db.totalCount ?? '-'}</TableCell>
                          <TableCell align="right">{db.count7d ?? '-'}</TableCell>
                          <TableCell align="right">{db.count30d ?? '-'}</TableCell>
                          <TableCell>{formatDate(db.newestTimestamp)}</TableCell>
                          <TableCell align="right">{humanBytes(db.totalSizeBytes)}</TableCell>
                          <TableCell>
                            <Chip
                              label={db.lastStatus ?? '-'}
                              color={statusColor(db.lastStatus)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {databases.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No database backup data available.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {activeTab === 1 && (
                <TableContainer
                  id="recent-activity-panel"
                  role="tabpanel"
                  aria-labelledby="recent-activity-tab"
                  sx={{ overflowX: 'auto', overflowY: 'auto', height: '100%' }}
                >
                  <Table size="small" aria-label="recent activity table" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>Database</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentLogs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No recent log records found.
                          </TableCell>
                        </TableRow>
                      )}
                      {recentLogs.map((log, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{formatDate(log.time || log.timestamp)}</TableCell>
                          <TableCell>{log.safe_db || log.db || '-'}</TableCell>
                          <TableCell>{log.file ? 'backup' : 'event'}</TableCell>
                          <TableCell>
                            <Chip
                              label={log.status || log.level || '-'}
                              color={statusColor(log.status || log.level)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {log.error || log.file || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>
        </>
      )}

      <Snackbar
        open={notice.open}
        autoHideDuration={3500}
        onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={notice.severity}
          variant="filled"
          onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
